import React from "react";
import { WebsiteBlock } from "../../../cores/websitePage/interfaces/WebsiteBlock";
import { WebsitePageWidgetOptions } from "./htmlPreview.utils";
import { NavbarBlock } from "./NavbarBlock";
import { HtmlPageBlock } from "./HtmlPageBlock";
import { ContactFormBlock } from "./ContactFormBlock";
import "./HtmlPageBlock.scss";

interface Props {
  block: WebsiteBlock;
  isEditing?: boolean;
  onSelect?: (blockId: string) => void;
  onUpdate?: (block: WebsiteBlock) => void;
  selectedBlockId?: string | null;
  widgets?: WebsitePageWidgetOptions;
}

export function BlockRenderer({ block, isEditing, onSelect, onUpdate, selectedBlockId, widgets }: Props) {
  const isSelected = selectedBlockId === block.id;
  const style = block.styles || {};
  const wrapperStyle: React.CSSProperties = {
    backgroundColor: block.type === "navbar" || block.type === "html-page" ? undefined : style.backgroundColor,
    color: style.color,
    textAlign: style.textAlign,
    padding: block.type === "navbar" || block.type === "html-page" ? 0 : style.padding,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    outline: isSelected ? "2px solid #4361ee" : undefined,
    cursor: isEditing ? "pointer" : undefined,
    position: "relative",
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isEditing && onSelect) {
      e.stopPropagation();
      onSelect(block.id);
    }
  };

  // Inline editing (Wix-style): click on the text in the canvas and type directly.
  const editable = (value: string | undefined, commit: (v: string) => void): React.ReactNode => {
    if (!isEditing || !onUpdate) return value;
    return (
      <span
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        onBlur={(e) => {
          const v = e.currentTarget.innerText.replace(/\n+$/, "");
          if (v !== (value || "")) commit(v);
        }}
        style={{ outline: "none", cursor: "text", minWidth: 10, display: "inline-block" }}
      >
        {value}
      </span>
    );
  };

  const renderContent = () => {
    switch (block.type) {
      case "navbar":
        return (
          <NavbarBlock block={block} isEditing={isEditing} onUpdate={onUpdate} />
        );
      case "header":
        const Tag = `h${block.level || 2}` as keyof JSX.IntrinsicElements;
        return <Tag>{editable(block.content, (v) => onUpdate?.({ ...block, content: v }))}</Tag>;
      case "text":
        return (
          <p style={{ whiteSpace: "pre-wrap" }}>
            {editable(block.content, (v) => onUpdate?.({ ...block, content: v }))}
          </p>
        );
      case "image":
        return (
          <div style={{ textAlign: (style.textAlign as React.CSSProperties["textAlign"]) || "center" }}>
            <img
              src={block.src}
              alt={block.alt || ""}
              style={{ maxWidth: block.width || "100%", borderRadius: block.borderRadius || 0 }}
            />
          </div>
        );
      case "button":
        return (
          <div style={{ textAlign: (style.textAlign as React.CSSProperties["textAlign"]) || "center" }}>
            <span
              style={{
                display: "inline-block",
                padding: "12px 28px",
                borderRadius: 8,
                background: block.backgroundColor || "#4361ee",
                color: block.color || "#fff",
                fontWeight: 600,
              }}
            >
              {editable(block.label, (v) => onUpdate?.({ ...block, label: v }))}
            </span>
          </div>
        );
      case "hero":
        return (
          <div
            className="wp-block-hero"
            style={{
              padding: "64px 24px",
              textAlign: "center",
              background: block.backgroundImage
                ? `url(${block.backgroundImage}) center/cover`
                : block.backgroundColor || "#4361ee",
              color: "#fff",
            }}
          >
            <h1 style={{ fontSize: "2.5rem", marginBottom: 16 }}>
              {editable(block.title, (v) => onUpdate?.({ ...block, title: v }))}
            </h1>
            <p style={{ fontSize: "1.2rem", marginBottom: 24 }}>
              {editable(block.subtitle, (v) => onUpdate?.({ ...block, subtitle: v }))}
            </p>
            {block.buttonLabel && (
              <span
                style={{
                  display: "inline-block",
                  padding: "12px 28px",
                  borderRadius: 8,
                  background: block.buttonColor || "#fff",
                  color: block.buttonTextColor || "#4361ee",
                  fontWeight: 600,
                }}
              >
                {editable(block.buttonLabel, (v) => onUpdate?.({ ...block, buttonLabel: v }))}
              </span>
            )}
          </div>
        );
      case "columns":
        return (
          <div className="wp-block-columns" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {(block.columns || []).map((col, i) => (
              <div key={i} className="wp-block-columns__col" style={{ flex: 1, minWidth: 180 }}>
                {(col.blocks || []).map((child) => (
                  <BlockRenderer
                    key={child.id}
                    block={child}
                    isEditing={isEditing}
                    onSelect={onSelect}
                    onUpdate={onUpdate}
                    selectedBlockId={selectedBlockId}
                    widgets={widgets}
                  />
                ))}
              </div>
            ))}
          </div>
        );
      case "gallery":
        return (
          <div
            className="wp-block-gallery"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 12,
            }}
          >
            {(block.images || []).map((img, i) => (
              <img key={i} src={img.src} alt={img.alt || ""} style={{ width: "100%", borderRadius: 8 }} />
            ))}
          </div>
        );
      case "video":
        return (
          <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
            <iframe
              src={block.embedUrl}
              title="video"
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
            />
          </div>
        );
      case "testimonials":
        return (
          <div className="wp-block-testimonials" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {(block.items || []).map((item, i) => (
              <div key={i} className="wp-block-testimonials__item" style={{ flex: 1, minWidth: 180, background: "#f8f9fa", padding: 16, borderRadius: 8 }}>
                <p>"{item.quote}"</p>
                <strong>{item.author}</strong>
                {item.role && <div><small>{item.role}</small></div>}
              </div>
            ))}
          </div>
        );
      case "faq":
        return (
          <div>
            {(block.items || []).map((item, i) => (
              <div key={i} style={{ borderBottom: "1px solid #eee", padding: "12px 0" }}>
                <div style={{ fontWeight: 600 }}>{item.question}</div>
                <div>{item.answer}</div>
              </div>
            ))}
          </div>
        );
      case "cta":
        return (
          <div
            className="wp-block-cta"
            style={{
              textAlign: "center",
              padding: "48px 24px",
              borderRadius: 8,
              background: block.backgroundColor || "#1a1a2e",
              color: block.color || "#fff",
            }}
          >
            <h2>{editable(block.title, (v) => onUpdate?.({ ...block, title: v }))}</h2>
            <p>{editable(block.subtitle, (v) => onUpdate?.({ ...block, subtitle: v }))}</p>
            {block.buttonLabel && (
              <span
                style={{
                  display: "inline-block",
                  marginTop: 16,
                  padding: "12px 28px",
                  borderRadius: 8,
                  background: block.buttonColor || "#fff",
                  color: block.buttonTextColor || "#4361ee",
                  fontWeight: 600,
                }}
              >
                {editable(block.buttonLabel, (v) => onUpdate?.({ ...block, buttonLabel: v }))}
              </span>
            )}
          </div>
        );
      case "footer":
        return (
          <footer style={{ padding: 24, textAlign: "center", background: "#1a1a2e", color: "#fff" }}>
            {editable(block.content, (v) => onUpdate?.({ ...block, content: v }))}
          </footer>
        );
      case "contact-form":
        return (
          <ContactFormBlock
            companyId={widgets?.companyId}
            formId={widgets?.formId}
          />
        );
      case "spacer":
        return <div style={{ height: block.height || "48px" }} />;
      case "html-page":
        return <HtmlPageBlock block={block} widgets={widgets} />;
      default:
        return null;
    }
  };

  return (
    <div style={wrapperStyle} onClick={handleClick} data-block-id={block.id}>
      {renderContent()}
    </div>
  );
}
